import { useInfiniteQueryUsersNotesList } from '../../users.queries';
import {
  AccountDto,
  DeliveryDto,
  KnownDeliveryDtoDeliveryType,
  KnownDeliveryState,
  KnownNoteAttachedToDtoType,
  KnownOrderStatus,
  KnownSortOrder,
  NoteDto,
  OrderDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { NoteProps } from '../../../../ui/components/pure/note/note.component';
import { deliveryTypeToIconMap } from '../../../deliveries/maps/delivery-type-to-icon.map';
import { createPath, Paths } from '../../../../app/router/app.router';
import { Order } from '../../../../ui/components/pure/icons/Order.icon';
import { useQueryGetOrders } from '../../../orders/orders.queries';
import { useQueryGetAccounts } from '../../../team/accounts.queries';
import { useQueryGetDeliveries } from '../../../deliveries/deliveries.queries';
import { useMemo } from 'react';
import { UseInfiniteQueryResult } from 'react-query';

interface UserNotesHandlers {
  onLoadMoreNotes(): void;
}

export function useUserNotes(params: { sortOrder?: KnownSortOrder }, userId: string, projectId: string) {
  return useNotesWithRelatedResources(useInfiniteQueryUsersNotesList(userId, params), projectId);
}

type NotesListQuery = UseInfiniteQueryResult<{ items?: NoteDto[] }>;

/**
 * A person's notes as NoteProps, each linked to the delivery or order it was left on. Shared by any
 * notes list that includes notes on related resources — users and couriers.
 */
export function useNotesWithRelatedResources(notes: NotesListQuery, projectId: string) {
  const flattenedNotes = useMemo(
    () => (notes.data?.pages || []).map((page) => page.items || []).flat(),
    [notes.data?.pages]
  );

  const { deliveryIDs, orderIDs, accountIDs } = useMemo(() => {
    const deliveryIDs = new Set<string>();
    const orderIDs = new Set<string>();
    const accountIDs = new Set<string>();

    flattenedNotes.forEach((note) => {
      accountIDs.add(note.createdBy);
      if (note.attachedTo.type === KnownNoteAttachedToDtoType.Delivery) {
        deliveryIDs.add(note.attachedTo.id);
      }
      if (note.attachedTo.type === KnownNoteAttachedToDtoType.Order) {
        orderIDs.add(note.attachedTo.id);
      }
    });
    return { deliveryIDs: Array.from(deliveryIDs), orderIDs: Array.from(orderIDs), accountIDs: Array.from(accountIDs) };
  }, [flattenedNotes]);

  const deliveries = useQueryGetDeliveries(deliveryIDs);
  const orders = useQueryGetOrders(orderIDs, projectId);
  const accounts = useQueryGetAccounts(accountIDs);

  const noteProps = useMemo(() => {
    return flattenedNotes.map((note) => {
      const account = accounts.find((account) => account.data?.id === note.createdBy);

      let delivery: DeliveryDto | undefined = undefined;
      let order: OrderDto | undefined = undefined;

      if (note.attachedTo.type === KnownNoteAttachedToDtoType.Delivery) {
        delivery = deliveries.find((delivery) => delivery.data?.id === note.attachedTo.id)?.data;
      }
      if (note.attachedTo.type === KnownNoteAttachedToDtoType.Order) {
        order = orders.find((order) => order.data?.id === note.attachedTo.id)?.data;
      }

      return mapNoteDtoToNoteProps(projectId, note, delivery, order, account?.data);
    });
  }, [flattenedNotes, accounts, projectId, deliveries, orders]);

  const handlers: UserNotesHandlers = {
    onLoadMoreNotes() {
      return notes.fetchNextPage();
    }
  };

  return {
    notes: noteProps,
    hasError: notes.isError,
    isLoading: notes.isLoading || notes.isFetching,
    handlers,
    hasNextPage: notes.hasNextPage
  };
}

function mapNoteDtoToNoteProps(
  projectID: string,
  note: NoteDto,
  delivery?: DeliveryDto,
  order?: OrderDto,
  account?: AccountDto
): NoteProps {
  return {
    date: note.createdTime.toISOString(),
    user: account?.firstName && account.lastName ? `${account.firstName} ${account.lastName}` : '',
    internal: note.isPrivate,
    content: note.content,
    child: delivery
      ? {
          Icon: deliveryTypeToIconMap[delivery.deliveryType as KnownDeliveryDtoDeliveryType],
          label: delivery.deliveryReference ?? '',
          path: createPath(Paths.delivery, { projectID: projectID, deliveryID: delivery.id }),
          deliveryStatus: delivery.state as KnownDeliveryState
        }
      : order
      ? {
          Icon: Order,
          label: order.orderNumber ?? '',
          path: createPath(Paths.order, { projectID: projectID, orderID: order.id }),
          orderStatus: order.status as KnownOrderStatus
        }
      : undefined
  };
}
